import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import CalendarDayCell from './CalendarDayCell'
import { rem } from 'polished'
import { calcGridPositionsForVerticalMonth, getCalendarMonthEmptyCells, getCalendarWeekStart, getCalendarWeekdayIndex, getLocalDate, orderCalendarWeekdays } from 'lib/datetime/calendarUtils'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import { getWeekdayNames } from 'lib/datetime/dateUtils'

const BaseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`

const Grid = styled(BaseGrid)`
  position: relative;
  margin-top: ${rem(8)};
`

const EmptyCell = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(8)} ${rem(12)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  box-shadow: 0 -1px 0 0 ${props => props.theme.palette.neutral.surface.lightForeground},
    1px 0 0 0 ${props => props.theme.palette.neutral.surface.lightForeground},
    1px 0 0 0 ${props => props.theme.palette.neutral.surface.lightForeground} inset,
    0 -1px 0 0 ${props => props.theme.palette.neutral.surface.lightForeground} inset;
`

interface Props {
  onDatesChange: (day: App.BedbankCalendarDay) => void
  checkIn?: string
  checkOut?: string
  days: Array<App.BedbankCalendarDay>
  className?: string
  duration: number
  flights: boolean
  adultsNumber: number
  roomsNumber: number
  displayPricingAsPerPerson: boolean
}

function CalendarDaysMonth(props: Props) {
  const {
    className,
    days,
    checkIn,
    checkOut,
    duration,
    flights,
    onDatesChange,
    adultsNumber,
    roomsNumber,
    displayPricingAsPerPerson,
  } = props

  const { locale: dateFormattingLocale } = useIntl()
  const weekStart = getCalendarWeekStart(dateFormattingLocale)
  const dayNames = useMemo(() => {
    const weekdayNames = getWeekdayNames(dateFormattingLocale)
    return orderCalendarWeekdays([
      weekdayNames.sun,
      weekdayNames.mon,
      weekdayNames.tue,
      weekdayNames.wed,
      weekdayNames.thu,
      weekdayNames.fri,
      weekdayNames.sat,
    ], weekStart)
  }, [dateFormattingLocale, weekStart])

  const firstDate = days[0]

  const startCellStyle = useMemo(() => ({
    gridColumnStart: getCalendarWeekdayIndex(getLocalDate(firstDate.checkIn), weekStart) + 1,
  }), [firstDate, weekStart])

  const emptyCells = useMemo(() => getCalendarMonthEmptyCells(firstDate, weekStart), [firstDate, weekStart])

  const betweenRange = useMemo(() => {
    if (checkIn && checkOut) {
      return calcGridPositionsForVerticalMonth(
        getLocalDate(firstDate.checkIn),
        getLocalDate(checkIn),
        getLocalDate(checkOut),
      ).range
    }
    return undefined
  }, [checkIn, checkOut, firstDate])

  const onDayClick = useCallback((day: App.BedbankCalendarDay) => {
    onDatesChange(day)
  }, [onDatesChange])

  return (
    <div className={className}>
      <BaseGrid>
        {dayNames.map((day) => (
          <Caption variant="large" as="abbr" title={day.long} key={day.long}>{day.short}</Caption>
        ))}
      </BaseGrid>
      <Grid>
        {emptyCells.map((cell, index) =>
          <EmptyCell style={{ gridColumnStart: cell.column }} key={index}>
            <Heading colour="light-foreground-alt" variant="heading6">{cell.day}</Heading>
          </EmptyCell>,
        )}
        {days.map((day, index) => {
          const isCheckIn = day.checkIn === checkIn
          const isCheckOut = day.checkIn === checkOut
          const isBetweenDay = !!(betweenRange && (index + 1) >= betweenRange.start && index < (betweenRange.end - 1))
          const isInSelectedRange = !!(checkIn && checkOut) && (isCheckIn || isCheckOut || isBetweenDay)

          return <CalendarDayCell
            key={day.checkIn}
            duration={duration}
            price={day.price}
            propertyFees={day.propertyFees}
            style={(index === 0 && emptyCells.length === 0) ? startCellStyle : undefined}
            day={day.day}
            flights={flights}
            flightPrice={day.flightPrice}
            hasPromo={day.hasPromo}
            isCorporate={day.isCorporate}
            isCheckIn={isCheckIn}
            isCheckOut={isCheckOut}
            isBetweenDay={isBetweenDay}
            isPriceVisible={!isInSelectedRange || isCheckIn}
            onClick={() => onDayClick(day)}
            displayPricingAsPerPerson={displayPricingAsPerPerson}
            adultsNumber={adultsNumber}
            roomsNumber={roomsNumber}
          />
        })}
      </Grid>
    </div>
  )
}

export default withOfferPageState((state) => ({
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
}), CalendarDaysMonth)
